import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';

/** One row of a CalculationTooltip breakdown — a label/value pair, optionally emphasised (e.g. a
 *  totals row) or a `---` separator row (rendered as an <hr>). */
export interface TooltipBreakdownRow {
    label: string;
    value: number | string;
    emphasis?: boolean;
}

interface CalculationTooltipProps {
    title: React.ReactNode;
    data?: TooltipBreakdownRow[];
    children: React.ReactNode;
}

const CalculationTooltip = ({ title, data, children }: CalculationTooltipProps) => {
    const [show, setShow] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    useLayoutEffect(() => {
        if (show && wrapperRef.current && tooltipRef.current) {
            const wrapperRect = wrapperRef.current.getBoundingClientRect();
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const spaceRight = window.innerWidth - wrapperRect.right;
            const spaceLeft = wrapperRect.left;

            if (spaceRight < tooltipRect.width / 2) {
                tooltipRef.current.style.left = `${window.innerWidth - tooltipRect.width - 16}px`;
            }
            if (spaceLeft < tooltipRect.width / 2) {
                tooltipRef.current.style.left = `16px`;
            }
        }
    }, [show]);

    useEffect(() => {
        if (show && wrapperRef.current && tooltipRef.current) {
            const rect = wrapperRef.current.getBoundingClientRect();
            const tooltipHeight = tooltipRef.current.offsetHeight || 180;
            const spaceBelow = window.innerHeight - rect.bottom;
            // Prefer below; if not enough room, show above. Then clamp so tooltip stays in viewport (no scrollbar).
            let top = spaceBelow >= tooltipHeight + 16 ? rect.bottom + 16 : rect.top - tooltipHeight - 16;
            if (top < 16) top = 16;
            if (top + tooltipHeight > window.innerHeight - 16) top = window.innerHeight - tooltipHeight - 16;
            setCoords({
                top,
                left: rect.left + rect.width / 2
            });
        }
    }, [show]);

    if (!Array.isArray(data)) return <>{children}</>;
    return (
        <div
            className="relative"
            ref={wrapperRef}
            onMouseEnter={() => setShow(true)}
            onMouseLeave={() => setShow(false)}
        >
            {children}
            {show && typeof window !== 'undefined' && window.document &&
                createPortal(
                    <div
                        ref={tooltipRef}
                        style={{
                            position: 'fixed',
                            top: coords.top,
                            left: coords.left,
                            transform: 'translateX(-50%)',
                            zIndex: 10001,
                            width: '340px',
                            maxWidth: '90vw',
                            pointerEvents: 'auto'
                        }}
                        className="p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg"
                    >
                        <h4 className="font-bold text-sm mb-2 text-blue-300">{title}</h4>
                        <div className="space-y-1">
                            {data.map((item, index) => {
                                let displayValue = item.value;
                                if (typeof displayValue === 'number' && !isNaN(displayValue)) {
                                    displayValue = Math.round(displayValue).toLocaleString();
                                }
                                if (item.emphasis) {
                                    const isDeficit = item.label === 'Deficit funded from';
                                    return (
                                        <div
                                            key={`emphasis-${item.label}-${index}`}
                                            className={`mt-2 pt-2 border-t border-gray-600 ${
                                                isDeficit ? 'bg-red-900/20 rounded px-2 py-1' : 'bg-green-900/20 rounded px-2 py-1'
                                            }`}
                                        >
                                            <div className="text-[10px] uppercase tracking-wide text-gray-300 mb-0.5">{item.label}</div>
                                            <div className={`font-semibold ${
                                                isDeficit ? 'text-red-300' : 'text-green-300'
                                            }`}>
                                                {displayValue}
                                            </div>
                                        </div>
                                    );
                                }
                                return item.label.startsWith('---') ? (
                                    <hr key={`hr-${item.label}-${index}`} className="border-gray-600 my-1" />
                                ) : (
                                    <div key={`row-${item.label}-${index}`} className="flex justify-between">
                                        <span className="text-gray-400">{item.label}:</span>
                                        <span className="font-mono font-semibold">{displayValue}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>,
                    document.body
                )
            }
        </div>
    );
};

export default CalculationTooltip;
